import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { MemoizedSelector, Store } from '@ngrx/store';
import { Observable, catchError, mergeMap, take } from 'rxjs';
import { BoardMigrationStatus } from '../model/board-migration-status';
import { AppConfigService } from '../services/app-config.service';
import { BackendBoardsService } from '../services/backend/backend-boards.service';
import { migrationStatusActions } from '../store/migration-status/migration-status.actions';
import { selectActiveBoardMigrationStatus } from '../store/migration-status/migration-status.selectors';

type SelectActiveBoardMigrationStatus = MemoizedSelector<
  object,
  { boardId: string; migrationStatus: BoardMigrationStatus }
>;

const stringifyBooleans = (key: any, value: any): any => {
  if (typeof value === 'boolean') {
    return value ? 'True' : 'False';
  }
  return value;
};

const createCvMigrationStateHeader = (
  boardId: string,
  migrationStatus: BoardMigrationStatus
): { 'Cv-Migration-State': string } => {
  return {
    'Cv-Migration-State': JSON.stringify({ board_id: boardId, status: migrationStatus }, stringifyBooleans),
  };
};

export const migrationStatusInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const backendBoardsService: BackendBoardsService = inject(BackendBoardsService);
  const baseUrl: string = inject(AppConfigService).appConfig.azure.proxy_base_url;
  const store: Store = inject(Store);

  const isGetRequest = request.method === 'GET';
  const isCvApiRequest = request.url.includes(baseUrl);
  const shouldAppendMigrationStatusHeader = !isGetRequest && isCvApiRequest;

  if (!shouldAppendMigrationStatusHeader) {
    return next(request);
  }

  return store.select(<SelectActiveBoardMigrationStatus>selectActiveBoardMigrationStatus).pipe(
    take(1),
    mergeMap(({ boardId, migrationStatus }) => {
      /**
       * This is to cater for admin view requests, where we don't have a boardId in the route params
       */
      if (!boardId) {
        return next(request);
      }

      return next(
        request.clone({
          setHeaders: createCvMigrationStateHeader(boardId, migrationStatus),
        })
      ).pipe(
        catchError((error) => {
          /**
           * If we have a mismatch between the migration status in the frontend and the migration status in the backend,
           * we will get a 412 error. In that case, we refetch the migration status from the backend and rethrow the error.
           */
          if (error.status === 412) {
            return backendBoardsService.getBoardMigrationStatus(boardId).pipe(
              mergeMap((migrationStatus) => {
                store.dispatch(migrationStatusActions.loadedMigrationStatus({ boardId, migrationStatus }));
                throw error;
              })
            );
          }
          /**
           * If we get a 423 error, we know that a migration is in progress so we update the migration status inProgress property
           * and start the reloadMigrationStatusEffect interval
           */
          if (error.status === 423) {
            store.dispatch(migrationStatusActions.reloadMigrationStatus({ boardId }));
          }
          throw error;
        })
      );
    })
  );
};
